import React, { useState, useEffect } from 'react';
import { MessageCircle, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatTimeAgo } from '../../utils/formatters';
import { qaService } from '../../services/qaService';

export default function QASection({ listing }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [newQ, setNewQ] = useState('');
  const [answerFor, setAnswerFor] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!listing?.id) return;
    qaService.getByListing(listing.id).then(data => setQuestions(data || [])).catch(() => {});
  }, [listing?.id]);

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!newQ.trim() || submitting) return;
    setSubmitting(true);
    try {
      const result = await qaService.ask(listing.id, newQ.trim());
      setQuestions(prev => [result, ...prev]);
      setNewQ('');
    } catch {}
    finally { setSubmitting(false); }
  };

  const submitAnswer = async (questionId) => {
    if (!answerText.trim()) return;
    try {
      const result = await qaService.answer(questionId, answerText.trim());
      setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, answer: result } : q));
      setAnswerFor(null);
      setAnswerText('');
    } catch {}
  };

  const isSeller = user?.id === listing?.seller?.id;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5 text-apple-gray-mid" />
        <h3 className="font-semibold text-apple-gray-dark">{t('qa.title')} ({questions.length})</h3>
      </div>

      {listing?.expired && (
        <p className="text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-6">
          {t('price_countdown.expired')} 
        </p>
      )}

      {!listing?.expired && user && !isSeller && (
        <form onSubmit={submitQuestion} className="flex gap-2 mb-6">
          <input
            value={newQ}
            onChange={e => setNewQ(e.target.value)}
            placeholder={t('qa.placeholder')}
            className="input-field flex-1"
          />
          <button type="submit" disabled={submitting} className="btn-primary px-4 flex items-center gap-1.5 disabled:opacity-60">
            <Send className="w-4 h-4" /> {t('qa.send')}
          </button>
        </form>
      )}

      {!listing?.expired && !user && (
        <p className="text-sm text-apple-gray-mid bg-apple-gray rounded-xl px-4 py-3 mb-6">
          {t('qa.login_to_ask')} <a href="/login" className="text-apple-blue hover:underline">{t('qa.login_prompt')}</a>.
        </p>
      )}

      <div className="space-y-4">
        {questions.length === 0 && (
          <p className="text-sm text-apple-gray-mid text-center py-6">{t('qa.no_questions')}</p>
        )}
        {questions.map(q => (
          <div key={q.id} className="bg-apple-gray rounded-2xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-xs font-semibold text-apple-gray-dark">{q.askerName || q.user}</span>
                <span className="text-xs text-apple-gray-mid ml-2">{formatTimeAgo(q.createDate || q.time)}</span>
              </div>
            </div>
            <p className="text-sm text-apple-gray-dark mt-1">❓ {q.questionText || q.question}</p>

            {q.answer && (
              <div className="mt-3 ml-4 bg-white rounded-xl p-3 border-l-2 border-apple-blue">
                <p className="text-xs font-semibold text-apple-blue mb-1">{t('qa.seller_reply')}</p>
                <p className="text-sm text-apple-gray-dark">✅ {q.answer.answerText || q.answer}</p>
              </div>
            )}

            {!q.answer && isSeller && (
              answerFor === q.id ? (
                <div className="mt-3 flex gap-2">
                  <input
                    value={answerText}
                    onChange={e => setAnswerText(e.target.value)}
                    placeholder={t('qa.answer_placeholder')}
                    className="input-field flex-1 text-sm py-2"
                    autoFocus
                  />
                  <button onClick={() => submitAnswer(q.id)} className="btn-primary text-sm px-3">{t('qa.reply')}</button>
                  <button onClick={() => setAnswerFor(null)} className="btn-secondary text-sm px-3">{t('qa.cancel')}</button>
                </div>
              ) : (
                <button onClick={() => setAnswerFor(q.id)} className="mt-2 text-xs text-apple-blue hover:underline">{t('qa.reply')}</button>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
